'use client';

import { useState, useEffect } from 'react';
import { Cake, Clock, Calendar, Gift } from 'lucide-react';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { birthdayCountdownContent } from '../../data/birthday-countdown-content';

export default function BirthdayCountdownClient() {
  const [birthDate, setBirthDate] = useState('');
  const [countdown, setCountdown] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    nextAge: number;
  } | null>(null);

  useEffect(() => {
    if (!birthDate) return;

    const interval = setInterval(() => {
      const now = new Date();
      const birthday = new Date(birthDate);
      const currentYear = now.getFullYear();

      // Set next birthday
      let nextBirthday = new Date(currentYear, birthday.getMonth(), birthday.getDate());

      // If birthday has passed this year, set to next year
      if (nextBirthday < now) {
        nextBirthday = new Date(currentYear + 1, birthday.getMonth(), birthday.getDate());
      }

      const difference = nextBirthday.getTime() - now.getTime();

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        const nextAge = nextBirthday.getFullYear() - birthday.getFullYear();

        setCountdown({ days, hours, minutes, seconds, nextAge });
      } else {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0, nextAge: currentYear - birthday.getFullYear() });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [birthDate]);

  const reset = () => {
    setBirthDate('');
    setCountdown(null);
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-pink-100 rounded-full mb-4">
          <Cake className="h-8 w-8 text-pink-600" />
        </div>
        <ToolPageIntro title="Birthday Countdown Timer">
                Count down to your next birthday with our free birthday countdown timer. See exactly how many days, hours, minutes, and seconds until your special day.
            </ToolPageIntro>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
        <div className="mb-6">
          <label htmlFor="birth-date" className="block text-sm font-medium text-gray-700 mb-1">
            Your Birth Date
          </label>
          <input
            type="date"
            id="birth-date"
            value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
            max={today}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
          />
        </div>

        <div className="flex justify-center space-x-3">
          <button
            onClick={reset}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2"
          >
            Reset
          </button>
        </div>
      </div>

      {countdown && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 text-center">
            {countdown.days === 0 && countdown.hours === 0 && countdown.minutes === 0 && countdown.seconds === 0
              ? '🎉 Happy Birthday! 🎉'
              : 'Time Until Your Next Birthday'}
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-pink-50 p-4 rounded-lg text-center">
              <div className="text-3xl font-bold text-pink-600">{countdown.days}</div>
              <div className="text-sm text-gray-600">Days</div>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg text-center">
              <div className="text-3xl font-bold text-purple-600">{countdown.hours}</div>
              <div className="text-sm text-gray-600">Hours</div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <div className="text-3xl font-bold text-blue-600">{countdown.minutes}</div>
              <div className="text-sm text-gray-600">Minutes</div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg text-center">
              <div className="text-3xl font-bold text-green-600">{countdown.seconds}</div>
              <div className="text-sm text-gray-600">Seconds</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="flex items-center text-gray-700">
                <Gift className="h-5 w-5 mr-2 text-yellow-600" />
                <span className="text-sm">
                  <strong>Next Age:</strong> {countdown.nextAge} years old
                </span>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center text-gray-700">
                <Calendar className="h-5 w-5 mr-2 text-gray-600" />
                <span className="text-sm">
                  <strong>Current Age:</strong> {countdown.nextAge - 1} years old
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <InformationalContent
        title={birthdayCountdownContent.title}
        description={birthdayCountdownContent.description}
        content={birthdayCountdownContent.content}
        faqs={birthdayCountdownContent.faqs}
        relatedTools={birthdayCountdownContent.relatedTools}
      />
    </div>
  );
}
